/**
 * Работа с localStorage для истории поиска
 * # ⛓️ CURSOR_ANCHOR:search:history-storage
 * # ⛓️ CURSOR_ANCHOR:search:history-hierarchy
 *
 * Модель записи (v2):
 *   {
 *     id: "h_<ts>_<rand>",
 *     parentId: null | "h_...",
 *     query: string,         // что ввёл/кликнул пользователь
 *     displayTerm: string,   // заголовок последней раскрытой статьи (мутабельно пока !locked)
 *     termIndex: null|int,   // номер раскрытой статьи
 *     locked: boolean,       // true после появления первого ребёнка
 *     lang, dict, searchType, timestamp
 *   }
 *
 * Legacy записи (без id/parentId) автоматически мигрируются при чтении.
 */

// ---- ID генерация ----
function _genHistoryId() {
  var ts = Date.now().toString(36);
  var rnd = Math.random().toString(36).slice(2, 8);
  return 'h_' + ts + '_' + rnd;
}

// ---- URL sync для активной записи ----
// При reload страницы параметр ?hid=... восстанавливает CURRENT_HISTORY_ID,
// поэтому цепочка прояснения переживает F5 и шарится по ссылке.
function _syncHistoryIdToUrl(id) {
  try {
    var url = new URL(window.location.href);
    if (id) {
      if (url.searchParams.get('hid') === id) return;
      url.searchParams.set('hid', id);
    } else {
      if (!url.searchParams.has('hid')) return;
      url.searchParams.delete('hid');
    }
    window.history.replaceState(window.history.state, '', url.toString());
  } catch (e) {
    // replaceState может быть запрещён в edge-кейсах — не ломаем логику истории.
  }
}

// Проверка, отключена ли история
function isHistoryDisabled() {
  try {
    var key = typeof SEARCH_HISTORY_DISABLED_KEY !== 'undefined' ? SEARCH_HISTORY_DISABLED_KEY : window.SEARCH_HISTORY_DISABLED_KEY;
    return localStorage.getItem(key) === 'true';
  } catch (e) {
    console.error('[HISTORY] Error checking if history is disabled:', e);
    return false;
  }
}

// Сохранение настройки отключения истории
function setHistoryDisabled(disabled) {
  try {
    var key = typeof SEARCH_HISTORY_DISABLED_KEY !== 'undefined' ? SEARCH_HISTORY_DISABLED_KEY : window.SEARCH_HISTORY_DISABLED_KEY;
    if (disabled) {
      localStorage.setItem(key, 'true');
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.error('[HISTORY] Error setting history disabled:', e);
  }
}

// ---- Миграция ----
// Приводит запись к актуальной схеме. Возвращает true если запись была изменена.
function _migrateItem(item) {
  var changed = false;
  if (!item.id) { item.id = _genHistoryId(); changed = true; }
  if (item.parentId === undefined) { item.parentId = null; changed = true; }
  if (item.displayTerm === undefined) { item.displayTerm = item.term || ''; changed = true; }
  if (item.query === undefined) { item.query = item.term || ''; changed = true; }
  if (item.locked === undefined) {
    // Legacy записи замораживаем по умолчанию (вариант 4.1 a: становятся плоскими корнями).
    item.locked = true;
    changed = true;
  }
  if (item.termIndex === undefined) { item.termIndex = null; changed = true; }
  if (item.termGuid === undefined) { item.termGuid = null; changed = true; }

  // Миграция старых типов поиска
  if (item.searchType === 'exact' || item.searchType === 'broad' ||
      item.searchType === 'description' || item.searchType === '' ||
      item.searchType === 'broad-without-vector') {
    item.searchType = 'classical';
    changed = true;
  } else if (item.searchType === 'broad-with-vector') {
    item.searchType = 'smart';
    changed = true;
  }
  return changed;
}

// Получить историю из localStorage (с автомиграцией схемы)
function getHistory() {
  try {
    var key = typeof SEARCH_HISTORY_KEY !== 'undefined' ? SEARCH_HISTORY_KEY : window.SEARCH_HISTORY_KEY;
    var historyJson = localStorage.getItem(key);
    if (!historyJson) {
      return [];
    }
    var history = JSON.parse(historyJson);
    if (!Array.isArray(history)) return [];

    var mutated = false;
    history.forEach(function(item) {
      if (_migrateItem(item)) mutated = true;
    });

    // Пересохраняем один раз если были изменения при миграции
    if (mutated && !isHistoryDisabled()) {
      try {
        localStorage.setItem(key, JSON.stringify(history));
      } catch (e) {
        console.warn('[HISTORY] Failed to persist migration:', e);
      }
    }

    return history;
  } catch (e) {
    console.error('[HISTORY] Error reading history:', e);
    return [];
  }
}

// Сохранить историю в localStorage
function saveHistory(history) {
  if (isHistoryDisabled()) {
    return;
  }

  try {
    var maxItems = typeof MAX_HISTORY_ITEMS !== 'undefined' ? MAX_HISTORY_ITEMS : window.MAX_HISTORY_ITEMS;
    var key = typeof SEARCH_HISTORY_KEY !== 'undefined' ? SEARCH_HISTORY_KEY : window.SEARCH_HISTORY_KEY;

    if (history.length > (maxItems || 500)) {
      history = history.slice(-(maxItems || 500));
    }
    localStorage.setItem(key, JSON.stringify(history));
    // Account-sync hook (account-sync.js): schedules a debounced server push.
    document.dispatchEvent(new CustomEvent('slovary-state-mutated', { detail: { kind: 'history' } }));
  } catch (e) {
    console.error('[HISTORY] Error saving history:', e);
  }
}

// ---- Lookup утилиты ----
function getHistoryById(id) {
  if (!id) return null;
  var history = getHistory();
  for (var i = 0; i < history.length; i++) {
    if (history[i].id === id) return history[i];
  }
  return null;
}

function getChildren(parentId) {
  var history = getHistory();
  return history.filter(function(it) { return it.parentId === parentId; });
}

// Возвращает цепочку от корня до записи с id (включительно).
function getRootChain(id) {
  var chain = [];
  var history = getHistory();
  var map = {};
  history.forEach(function(it) { map[it.id] = it; });

  var cur = map[id];
  var guard = 0;
  while (cur && guard < 100) {
    chain.unshift(cur);
    if (!cur.parentId) break;
    cur = map[cur.parentId];
    guard++;
  }
  return chain;
}

// ---- Добавление записи ----
/**
 * Добавляет запись в историю. Возвращает id новой записи (или null, если добавление не состоялось).
 *
 * Поведение parentId:
 *   - null → корневая запись (новая ветка).
 *   - "h_..." → дочерняя запись. Родитель будет помечен locked=true.
 *
 * Дубликаты: повторный поиск того же term+lang в рамках одной ветки (без parentId или с тем же parentId)
 * поднимает существующую запись в конец массива вместо создания дубликата.
 */
function addToHistory(searchTerm, lang, dictName, searchType, parentId) {
  if (isHistoryDisabled() || !searchTerm || !searchTerm.trim()) {
    return null;
  }

  var trimmedTerm = searchTerm.trim();
  var history = getHistory();
  parentId = parentId || null;

  // Если есть parent — замораживаем его (первый ребёнок → locked).
  if (parentId) {
    for (var p = 0; p < history.length; p++) {
      if (history[p].id === parentId && !history[p].locked) {
        history[p].locked = true;
        break;
      }
    }
  }

  // Дедупликация в пределах той же ветки (тот же parentId).
  // Не трогаем записи из других веток — они остаются на своих местах.
  var existingIdx = -1;
  for (var i = 0; i < history.length; i++) {
    var it = history[i];
    if (it.parentId === parentId && it.query === trimmedTerm && it.lang === lang) {
      existingIdx = i;
      break;
    }
  }

  var id;
  if (existingIdx >= 0) {
    // Поднимаем существующую запись: сохраняем её id, обновляем timestamp и searchType/dict.
    var existing = history.splice(existingIdx, 1)[0];
    existing.timestamp = Date.now();
    existing.searchType = searchType || existing.searchType || '';
    existing.dict = dictName || existing.dict || '';
    // Если запись ещё не locked — можно обновить displayTerm/termIndex при следующем раскрытии.
    history.push(existing);
    id = existing.id;
  } else {
    id = _genHistoryId();
    history.push({
      id: id,
      parentId: parentId,
      query: trimmedTerm,
      displayTerm: trimmedTerm,
      termIndex: null,
      termGuid: null,
      locked: false,
      // legacy поле для обратной совместимости рендера
      term: trimmedTerm,
      lang: lang,
      dict: dictName || '',
      searchType: searchType || '',
      timestamp: Date.now()
    });
  }

  saveHistory(history);
  window.CURRENT_HISTORY_ID = id;
  setChainLastActive(id);
  _syncHistoryIdToUrl(id);
  return id;
}

/**
 * Обновить displayTerm/termIndex активной записи при раскрытии статьи.
 * Работает только если запись НЕ locked (до первого cross-link клика).
 * # ⛓️ CURSOR_ANCHOR:search:history-update-item
 */
function updateActiveHistoryDisplay(termIndex, displayTerm, termGuid) {
  if (isHistoryDisabled()) return;
  var activeId = window.CURRENT_HISTORY_ID;
  if (!activeId) return;

  var history = getHistory();
  var item = null;
  for (var i = 0; i < history.length; i++) {
    if (history[i].id === activeId) { item = history[i]; break; }
  }
  if (!item) return;

  // Check if this is a non-leaf item (has children in the chain)
  var hasChildren = false;
  for (var c = 0; c < history.length; c++) {
    if (history[c].parentId === activeId) { hasChildren = true; break; }
  }

  // Non-leaf items (always locked): buffer changes instead of saving.
  // User must click "Save" in chain panel to commit.
  // Only buffer if something actually CHANGED vs the stored record.
  if (hasChildren) {
    var newTermIndex = (termIndex !== null && termIndex !== undefined) ? termIndex : item.termIndex;
    var newDisplayTerm = (displayTerm && displayTerm.trim()) ? displayTerm.trim() : item.displayTerm;
    var newTermGuid = (termGuid && typeof termGuid === 'string') ? termGuid : (item.termGuid || null);

    // Only show save button if user deliberately expanded a DIFFERENT article.
    // Compare termIndex as numbers (avoid string/number mismatch).
    // Compare termGuid only if both are non-empty strings.
    var storedIdx = Number(item.termIndex) || 0;
    var newIdx = Number(newTermIndex) || 0;
    var idxChanged = (newIdx > 0 && storedIdx > 0 && newIdx !== storedIdx);
    var guidChanged = (newTermGuid && item.termGuid && newTermGuid !== item.termGuid);

    if (idxChanged || guidChanged) {
      window._CHAIN_PENDING_SAVE = {
        id: activeId,
        termIndex: newTermIndex,
        displayTerm: newDisplayTerm,
        termGuid: newTermGuid
      };
      if (window.CHAIN_PANEL && window.CHAIN_PANEL.showPendingSave) {
        window.CHAIN_PANEL.showPendingSave(activeId);
      }
    } else if (window._CHAIN_PENDING_SAVE && window._CHAIN_PENDING_SAVE.id === activeId) {
      // Пользователь вернулся к исходному (committed) состоянию — убираем дискетку.
      window._CHAIN_PENDING_SAVE = null;
      if (window.CHAIN_PANEL && window.CHAIN_PANEL.hidePendingSave) {
        window.CHAIN_PANEL.hidePendingSave();
      }
    }
    return;
  }

  // Leaf items: save immediately as before.
  // Note: don't check item.locked here — locked may be stale
  // (e.g. after all children were deleted). hasChildren check above
  // already confirmed this is a true leaf.
  var changed = false;
  if (termIndex !== null && termIndex !== undefined) {
    item.termIndex = termIndex;
    changed = true;
  }
  if (termGuid && typeof termGuid === 'string') {
    if (item.termGuid !== termGuid) {
      item.termGuid = termGuid;
      changed = true;
    }
  }
  if (displayTerm && typeof displayTerm === 'string' && displayTerm.trim()) {
    var dt = displayTerm.trim();
    if (item.displayTerm !== dt) {
      item.displayTerm = dt;
      changed = true;
    }
  }
  if (changed) {
    saveHistory(history);
    // Refresh breadcrumb + панели (если открыта). skipAutoOpen — обновление
    // displayTerm на leaf не должно триггерить авто-открытие панели.
    if (window.CHAIN_PANEL && window.CHAIN_PANEL.update) {
      window.CHAIN_PANEL.update(activeId, { skipAutoOpen: true });
    }
  }
}

/**
 * Legacy shim: старый API, обновляющий только termIndex последней записи.
 * Делегирует в updateActiveHistoryDisplay. Оставлен для обратной совместимости
 * с местами, которые ещё не переведены на новый API.
 */
function updateLastHistoryItem(termIndex) {
  // Пытаемся извлечь displayTerm из DOM (текущая раскрытая статья).
  var displayTerm = null;
  try {
    var expanded = document.querySelector('.zaglavie.expanded');
    if (expanded) {
      var termsEl = expanded.querySelector('.terms');
      displayTerm = termsEl ? (termsEl.textContent || '').trim() : (expanded.textContent || '').trim();
    }
  } catch (e) {}

  // Если активной записи нет — fallback на старое поведение:
  // обновляем termIndex последней записи если term совпадает с текущим поиском.
  if (!window.CURRENT_HISTORY_ID) {
    var history = getHistory();
    if (history.length === 0) return;
    var lastItem = history[history.length - 1];
    var searchInput = document.getElementById('search');
    var currentTerm = searchInput ? searchInput.value.trim() : '';
    var currentLang = '';
    if (window.SEARCH_LANGUAGE && typeof window.SEARCH_LANGUAGE.getCurrentLang === 'function') {
      currentLang = window.SEARCH_LANGUAGE.getCurrentLang();
    }
    if (lastItem.query === currentTerm && (!currentLang || lastItem.lang === currentLang)) {
      window.CURRENT_HISTORY_ID = lastItem.id;
    }
  }

  updateActiveHistoryDisplay(termIndex, displayTerm);
}

// Явно заморозить запись (вызывается перед созданием ребёнка из search.js).
function lockHistoryItem(id) {
  if (!id) return;
  var history = getHistory();
  var changed = false;
  for (var i = 0; i < history.length; i++) {
    if (history[i].id === id && !history[i].locked) {
      history[i].locked = true;
      changed = true;
      break;
    }
  }
  if (changed) saveHistory(history);
}

// Отвязать запись от родителя (сделать корневой).
function detachHistoryItem(id) {
  if (!id) return;
  var history = getHistory();
  for (var i = 0; i < history.length; i++) {
    if (history[i].id === id) {
      history[i].parentId = null;
      saveHistory(history);
      return;
    }
  }
}

// ---- Chain last-active tracking ----
// Запоминает, с каким термином пользователь в последний раз работал в рамках
// каждой цепочки (ключ — id корня). Используется при клике на свёрнутый корень
// цепочки в dropdown: вместо поиска по корню восстанавливается последний термин.
var CHAIN_LAST_ACTIVE_KEY = 'slovary-chain-last-active';

function _readChainLastActiveMap() {
  try {
    var raw = localStorage.getItem(CHAIN_LAST_ACTIVE_KEY);
    if (!raw) return {};
    var parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch (e) { return {}; }
}

function _writeChainLastActiveMap(map) {
  try { localStorage.setItem(CHAIN_LAST_ACTIVE_KEY, JSON.stringify(map || {})); }
  catch (e) {}
}

function _findRootId(itemId, history) {
  var byId = {};
  history.forEach(function(it) { byId[it.id] = it; });
  var walk = byId[itemId];
  var guard = 0;
  while (walk && walk.parentId && byId[walk.parentId] && guard < 100) {
    walk = byId[walk.parentId];
    guard++;
  }
  return walk ? walk.id : null;
}

function setChainLastActive(itemId) {
  if (!itemId) return;
  var history = getHistory();
  var rootId = _findRootId(itemId, history);
  if (!rootId) return;
  var map = _readChainLastActiveMap();
  map[rootId] = itemId;
  _writeChainLastActiveMap(map);
}

function getChainLastActive(rootId) {
  if (!rootId) return null;
  var history = getHistory();
  var byId = {};
  history.forEach(function(it) { byId[it.id] = it; });
  if (!byId[rootId]) return null;

  // Сначала — сохранённый вариант (если валиден и принадлежит этой цепочке).
  var map = _readChainLastActiveMap();
  var storedId = map[rootId];
  if (storedId && byId[storedId]) {
    var actualRoot = _findRootId(storedId, history);
    if (actualRoot === rootId) return storedId;
  }

  // Fallback — самый свежий по timestamp потомок; если потомков нет, сам корень.
  var candidates = [];
  function collect(parentId) {
    for (var i = 0; i < history.length; i++) {
      if (history[i].parentId === parentId) {
        candidates.push(history[i]);
        collect(history[i].id);
      }
    }
  }
  collect(rootId);
  if (candidates.length === 0) return rootId;
  candidates.sort(function(a, b) { return (b.timestamp || 0) - (a.timestamp || 0); });
  return candidates[0].id;
}

// Перепривязать запись к новому родителю (присоединить к цепочке).
// Предотвращает циклы: newParentId не может быть потомком id.
// Возвращает true при успехе, false если операция невозможна.
function reparentHistoryItem(id, newParentId) {
  if (!id || !newParentId || id === newParentId) return false;
  var history = getHistory();
  var byId = {};
  history.forEach(function(it) { byId[it.id] = it; });

  // Проверка на цикл: newParentId не должен быть потомком id.
  var walk = byId[newParentId];
  var guard = 0;
  while (walk && guard < 100) {
    if (walk.id === id) return false;
    walk = walk.parentId ? byId[walk.parentId] : null;
    guard++;
  }

  var found = false;
  for (var i = 0; i < history.length; i++) {
    if (history[i].id === id) {
      history[i].parentId = newParentId;
      found = true;
      break;
    }
  }
  if (!found) return false;

  // Замораживаем нового родителя (первый ребёнок → locked).
  for (var j = 0; j < history.length; j++) {
    if (history[j].id === newParentId && !history[j].locked) {
      history[j].locked = true;
      break;
    }
  }
  saveHistory(history);
  return true;
}

// Удалить запись и всех её потомков (каскад).
// Возвращает количество удалённых записей.
function deleteHistoryItem(id) {
  if (!id) return 0;
  var history = getHistory();

  // Собираем set всех id на удаление (DFS по parentId).
  var toDelete = {};
  function collectDescendants(parentId) {
    for (var i = 0; i < history.length; i++) {
      if (history[i].parentId === parentId && !toDelete[history[i].id]) {
        toDelete[history[i].id] = true;
        collectDescendants(history[i].id);
      }
    }
  }
  toDelete[id] = true;
  collectDescendants(id);

  // Before deleting: if active item will be removed, find nearest surviving ancestor.
  var redirectTarget = null;
  if (toDelete[window.CURRENT_HISTORY_ID]) {
    var byIdSnap = {};
    history.forEach(function(it) { byIdSnap[it.id] = it; });
    // Walk up from the explicitly deleted item to find a surviving parent
    var cur = byIdSnap[id];
    if (cur && cur.parentId && !toDelete[cur.parentId]) {
      redirectTarget = cur.parentId;
    } else if (cur && cur.parentId) {
      // Parent is also being deleted — walk further up
      var walk = byIdSnap[cur.parentId];
      var guard = 0;
      while (walk && guard < 100) {
        if (!toDelete[walk.id]) { redirectTarget = walk.id; break; }
        walk = walk.parentId ? byIdSnap[walk.parentId] : null;
        guard++;
      }
    }
  }

  var before = history.length;
  history = history.filter(function(it) { return !toDelete[it.id]; });
  saveHistory(history);

  // Account-sync hook: every cascade-deleted id needs a tombstone, otherwise
  // the record resurrects from another synced browser.
  document.dispatchEvent(new CustomEvent('slovary-state-mutated', {
    detail: { kind: 'history-delete', ids: Object.keys(toDelete) }
  }));

  // Если удалили активную запись — перенаправляем на ближайшего выжившего предка.
  if (toDelete[window.CURRENT_HISTORY_ID]) {
    // redirectTarget was computed before deletion (see above)
    if (redirectTarget) {
      window.CURRENT_HISTORY_ID = redirectTarget;
      _syncHistoryIdToUrl(redirectTarget);
    } else {
      window.CURRENT_HISTORY_ID = null;
      _syncHistoryIdToUrl(null);
    }
  }

  return before - history.length;
}

// ---- Частичная очистка: оставить N самых свежих цепочек ----

// Количество различных цепочек (корней) в истории.
function countHistoryChains() {
  var history = getHistory();
  var byId = {};
  history.forEach(function(it) { byId[it.id] = it; });
  var roots = {};
  history.forEach(function(it) {
    var walk = it, guard = 0;
    while (walk.parentId && byId[walk.parentId] && guard < 100) {
      walk = byId[walk.parentId];
      guard++;
    }
    roots[walk.id] = true;
  });
  return Object.keys(roots).length;
}

/**
 * Оставляет keepCount самых свежих цепочек (корень + все потомки), остальные
 * удаляет целиком. Свежесть цепочки — максимальный timestamp её поддерева.
 * Удалённые id получают tombstones через 'history-delete' (иначе при
 * привязанном Telegram цепочки воскреснут из другого браузера).
 * Возвращает количество удалённых записей.
 */
function trimHistoryChains(keepCount) {
  keepCount = keepCount || 30;
  var history = getHistory();
  if (history.length === 0) return 0;

  var byId = {};
  history.forEach(function(it) { byId[it.id] = it; });
  function rootOf(it) {
    var walk = it, guard = 0;
    while (walk.parentId && byId[walk.parentId] && guard < 100) {
      walk = byId[walk.parentId];
      guard++;
    }
    return walk.id;
  }

  // rootId -> max timestamp поддерева
  var chains = {};
  history.forEach(function(it) {
    var r = rootOf(it);
    var ts = it.timestamp || 0;
    if (!(r in chains) || ts > chains[r]) chains[r] = ts;
  });

  var rootIds = Object.keys(chains);
  if (rootIds.length <= keepCount) return 0;

  rootIds.sort(function(a, b) { return chains[b] - chains[a]; });
  var keep = {};
  rootIds.slice(0, keepCount).forEach(function(r) { keep[r] = true; });

  var toDelete = {};
  history.forEach(function(it) {
    if (!keep[rootOf(it)]) toDelete[it.id] = true;
  });

  var survivors = history.filter(function(it) { return !toDelete[it.id]; });
  var removed = history.length - survivors.length;
  if (removed === 0) return 0;

  saveHistory(survivors);
  document.dispatchEvent(new CustomEvent('slovary-state-mutated', {
    detail: { kind: 'history-delete', ids: Object.keys(toDelete) }
  }));

  if (toDelete[window.CURRENT_HISTORY_ID]) {
    window.CURRENT_HISTORY_ID = null;
    _syncHistoryIdToUrl(null);
  }
  return removed;
}

// Очистить историю
function clearHistory() {
  try {
    var key = typeof SEARCH_HISTORY_KEY !== 'undefined' ? SEARCH_HISTORY_KEY : window.SEARCH_HISTORY_KEY;
    localStorage.removeItem(key);
    window.CURRENT_HISTORY_ID = null;
    _syncHistoryIdToUrl(null);
    // Account-sync hook: a clear must mask ALL earlier records server-side.
    document.dispatchEvent(new CustomEvent('slovary-state-mutated', { detail: { kind: 'history-clear' } }));
  } catch (e) {
    console.error('[HISTORY] Error clearing history:', e);
  }
}

// Читает ?hid=... из URL и, если такая запись существует, восстанавливает
// CURRENT_HISTORY_ID. Вызывается один раз при загрузке страницы (history.js init).
function restoreActiveHistoryFromUrl() {
  try {
    var url = new URL(window.location.href);
    var hid = url.searchParams.get('hid');
    if (!hid) return null;
    var item = getHistoryById(hid);
    if (!item) {
      // Стейл hid: запись удалена/не существует — чистим URL.
      _syncHistoryIdToUrl(null);
      return null;
    }
    window.CURRENT_HISTORY_ID = hid;
    setChainLastActive(hid);
    return hid;
  } catch (e) {
    return null;
  }
}

// Глобальное состояние активной записи
if (typeof window.CURRENT_HISTORY_ID === 'undefined') {
  window.CURRENT_HISTORY_ID = null;
}

// Экспорты
window.addToHistory = addToHistory;
window.updateLastHistoryItem = updateLastHistoryItem;
/**
 * Commits buffered pending save for a non-leaf chain item.
 * Called when user clicks "Save" button in chain panel.
 */
function commitPendingSave() {
  var pending = window._CHAIN_PENDING_SAVE;
  if (!pending || !pending.id) return false;

  var history = getHistory();
  var committed = false;
  for (var i = 0; i < history.length; i++) {
    if (history[i].id !== pending.id) continue;
    if (pending.termIndex !== null && pending.termIndex !== undefined) {
      history[i].termIndex = pending.termIndex;
    }
    if (pending.displayTerm) {
      history[i].displayTerm = pending.displayTerm;
    }
    if (pending.termGuid) {
      history[i].termGuid = pending.termGuid;
    }
    if (pending.searchType) {
      history[i].searchType = pending.searchType;
    }
    committed = true;
    break;
  }

  if (committed) {
    saveHistory(history);
    window._CHAIN_PENDING_SAVE = null;
    if (window.CHAIN_PANEL && window.CHAIN_PANEL.isOpen && window.CHAIN_PANEL.isOpen()) {
      window.CHAIN_PANEL.update(pending.id);
    }
  }
  return committed;
}

/**
 * Discards buffered pending save (user navigated away without saving).
 */
function clearPendingSave() {
  window._CHAIN_PENDING_SAVE = null;
}

window.updateActiveHistoryDisplay = updateActiveHistoryDisplay;
window.commitPendingSave = commitPendingSave;
window.clearPendingSave = clearPendingSave;
window.lockHistoryItem = lockHistoryItem;
window.getHistory = getHistory;
window.getHistoryById = getHistoryById;
window.getHistoryChildren = getChildren;
window.getHistoryRootChain = getRootChain;
window.saveHistory = saveHistory;
window.clearHistory = clearHistory;
window.trimHistoryChains = trimHistoryChains;
window.countHistoryChains = countHistoryChains;
window.deleteHistoryItem = deleteHistoryItem;
window.detachHistoryItem = detachHistoryItem;
window.reparentHistoryItem = reparentHistoryItem;
window.setChainLastActive = setChainLastActive;
window.getChainLastActive = getChainLastActive;
window.isHistoryDisabled = isHistoryDisabled;
window.setHistoryDisabled = setHistoryDisabled;
window.syncHistoryIdToUrl = _syncHistoryIdToUrl;
window.restoreActiveHistoryFromUrl = restoreActiveHistoryFromUrl;
